import { ApproverType, EventStatus } from './types';
import { sentenceCase } from 'change-case';
import i18n from './i18n/config';

// Common Text
export const Save = i18n.t('common.actions.save');
export const Edit = i18n.t('common.actions.edit');
export const SaveChanges = i18n.t('common.actions.saveChanges');
export const SaveAndExit = i18n.t('common.actions.saveAndExit');
export const Cancel = i18n.t('common.actions.cancel');
export const Canceled = i18n.t('common.statuses.canceled');
export const CancelEvent = i18n.t('events.actions.cancelEvent');
export const Back = i18n.t('common.actions.back');
export const Delete = i18n.t('common.actions.delete');
export const Required = i18n.t('common.statuses.required');
export const Previous = i18n.t('common.actions.previous');
export const Next = i18n.t('common.actions.next');
export const DefaultErrorMessage = i18n.t('common.errors.defaultErrorMessage');
export const TimeZoneMissingMessage = i18n.t('common.errors.timeZoneMissingMessage');
export const InvalidCredentials = i18n.t('common.errors.invalidCredentials');
export const Reject = i18n.t('common.actions.reject');
export const Rejected = i18n.t('common.statuses.rejected');
export const Accept = i18n.t('common.actions.accept');
export const Accepted = i18n.t('common.statuses.accepted');
export const Success = i18n.t('common.statuses.success');
export const Failed = i18n.t('common.statuses.failed');
export const EmailPlaceholder = i18n.t('common.placeholders.email');
export const PasswordPlaceholder = i18n.t('common.placeholders.password');
export const None = i18n.t('common.labels.none');
export const AssignedTo = i18n.t('common.labels.assignedTo');
export const EventTitle = i18n.t('common.labels.eventTitle');
export const EventId = i18n.t('common.labels.eventId');
export const EventType = i18n.t('common.labels.eventType');
export const Probability = i18n.t('common.labels.probability');
export const Severity = i18n.t('common.labels.severity');
export const Tags = i18n.t('common.labels.tags');
export const Attachments = i18n.t('common.labels.attachments');
export const Comments = i18n.t('common.labels.comments');
export const LastModified = i18n.t('common.labels.lastModified');
export const DueDate = i18n.t('common.labels.dueDate');
export const FilterByStatus = i18n.t('common.filters.filterByStatus');
export const FilterByEventType = i18n.t('common.filters.filterByEventType');
export const FilterByCreatedDate = i18n.t('common.filters.filterByCreatedDate');
export const FilterByDueDate = i18n.t('common.filters.filterByDueDate');
export const FilterByResolvedDate = i18n.t('common.filters.filterByResolvedDate');
export const UsersList = i18n.t('common.labels.usersList');
export const CreatedBefore = i18n.t('common.labels.createdBefore');
export const CreatedAfter = i18n.t('common.labels.createdAfter');
export const Overdue = i18n.t('common.labels.overdue');
export const OnTime = i18n.t('common.labels.onTime');

/**
 * Core Error Messages
 * These messages go along with errors that may occur when loading "low level"
 * pieces of the application
 */
export const CurrentCompanyRoleError = i18n.t('common.errors.currentCompanyRoleError');

// Event Templates
export const EventTemplateCreated = i18n.t('templates.messages.eventTemplateCreated');

export const TemplateFieldControlDescription = i18n.t('templates.helpText.templateFieldControlDescription');
export const TemplateFieldControlDescriptionForEventCapture = i18n.t(
  'templates.helpText.templateFieldControlDescriptionForEventCapture',
);
export const DefaultAssignee = i18n.t('templates.labels.defaultAssignee');
export const DefaultEventOwner = i18n.t('templates.labels.defaultEventOwner');
export const DefaultAssigneeHelper = i18n.t('templates.helpText.defaultAssigneeHelper');
export const DefaultEventOwnerHelper = i18n.t('templates.helpText.defaultEventOwnerHelper');
export const EventTemplateCreatedFailTitle = i18n.t('templates.messages.eventTemplateCreatedFailTitle');
export const CreateTemplateDraft = i18n.t('templates.actions.createTemplateDraft');
export const MakeEffective = i18n.t('templates.actions.makeEffective');
export const AddStep = i18n.t('templates.actions.addStep');
export const MakeEffectiveModalHeadingText = i18n.t('templates.modals.makeEffectiveModalHeadingText');
export const MakeEffectiveModalSubText = i18n.t('templates.modals.makeEffectiveModalSubText');
export const MakeEffectiveSuccessMessage = i18n.t('templates.messages.makeEffectiveSuccessMessage');
export const EventTemplateUpdateFailTitle = i18n.t('templates.messages.eventTemplateUpdateFailTitle');
export const DeleteDraft = i18n.t('templates.actions.deleteDraft');
export const DeleteDraftConfirmationHeader = i18n.t('templates.modals.deleteDraftConfirmationHeader');
export const DeleteDraftConformationBody = i18n.t('templates.modals.deleteDraftConformationBody');
export const ChangeTemplateOwner = i18n.t('templates.actions.changeTemplateOwner');
export const ChangeEventTemplateOwnerSuccessMessage = i18n.t(
  'templates.messages.changeEventTemplateOwnerSuccessMessage',
);
export const CurrentEventTemplateOwnerLabel = i18n.t('templates.fields.currentEventTemplateOwnerLabel');
export const ReasonForChangeInputLabel = i18n.t('templates.fields.reasonForChangeInputLabel');
export const ReasonForChangePlaceholder = i18n.t('templates.fields.reasonForChangePlaceholder');

// Event Template exports
export const EventTemplateExport = i18n.t('common.actions.export');
export const EventTemplateExportModalTitle = i18n.t('templates.modals.exportModalTitle');
export const EventTemplateExportModalSubText = i18n.t('templates.modals.exportModalSubText');
export const EventTemplateExportCoverPageTitle = i18n.t('templates.modals.exportCoverPageTitle');
export const EventTemplateExportCoverPageSubText = i18n.t('templates.modals.exportCoverPageSubText');
export const EventTemplateExportIncludeCoverPage = i18n.t('templates.modals.exportIncludeCoverPage');

// Event Template statuses
export const TemplateDraft = i18n.t('templates.statuses.draft');
export const TemplateEffective = i18n.t('templates.statuses.effective');
export const TemplateArchived = i18n.t('templates.statuses.archived');
export const TemplateForApproval = i18n.t('templates.statuses.forApproval');
export const TemplateApprovalDeclined = i18n.t('templates.statuses.approvalDeclined');
export const TemplateSuperseded = i18n.t('templates.statuses.superseded');
export const TemplateAllActiveStatuses = i18n.t('templates.statuses.allActiveStatuses');

// Event content step templates
export const ResolveSection = (sectionTitle: string) => {
  return i18n.t('templates.content.resolveSection', { sectionTitle });
};
export const UpdateTemplate = i18n.t('templates.actions.updateTemplate');
export const RenameStep = i18n.t('templates.actions.renameStep');
export const ChangeValidationStepType = i18n.t('templates.actions.changeValidationStepType');
export const DeleteStep = i18n.t('templates.actions.deleteStep');
export const CreateEventTemplate = i18n.t('templates.actions.createEventTemplate');
export const CreateTemplate = i18n.t('templates.actions.createTemplate');
export const AddSection = i18n.t('templates.actions.addSection');
export const DuplicateTitlesAreNotAllowed = i18n.t('templates.validation.duplicateTitlesAreNotAllowed');
export const InSection = i18n.t('templates.labels.inSection');
export const FormSteps = i18n.t('templates.labels.formSteps');
export const DefaultSections = i18n.t('templates.labels.defaultSections');
export const SelectResolution = i18n.t('templates.actions.selectResolution');
export const EmptyTitlesAreNotAllowed = i18n.t('templates.validation.emptyTitlesAreNotAllowed');
export const DraftDeleted = i18n.t('common.notifications.draftDeleted');
export const DraftNotDeleted = i18n.t('common.notifications.draftNotDeleted');
export const EditContentTemplate = i18n.t('templates.actions.editContentTemplate');
export const InsertCodeBlock = i18n.t('templates.ckeditor.insertCodeBlock');
export const Paragraph = i18n.t('templates.ckeditor.paragraph');
export const Heading1 = i18n.t('templates.ckeditor.heading1');
export const Heading2 = i18n.t('templates.ckeditor.heading2');
export const Heading3 = i18n.t('templates.ckeditor.heading3');
export const Heading4 = i18n.t('templates.ckeditor.heading4');
export const Normal = i18n.t('templates.ckeditor.normal');
export const PleaseChooseAResolution = i18n.t('templates.messages.pleaseChooseAResolution');
export const NoFlagsReturnedInThisEnvironment = i18n.t('common.debug.noFlagsReturnedInThisEnvironment');
export const TemplateRestoredNotification = i18n.t('common.notifications.templateRestored');
export const TemplateRestoringFailedNotification = i18n.t('common.notifications.templateRestoringFailed');
export const TemplateArchivedNotification = i18n.t('common.notifications.templateArchived');
export const TemplateArchivingFailedNotification = i18n.t('common.notifications.templateArchivingFailed');

// Event tasks
export const CreateTask = i18n.t('tasks.actions.createTask');
export const ReopenTask = i18n.t('tasks.actions.reopenTask');
export const Comment = i18n.t('tasks.actions.comment');
export const UserReassigned = i18n.t('tasks.messages.userReassigned');
export const UserReassignedSuccessfully = i18n.t('tasks.messages.userReassignedSuccessfully');

// Event Form templates
export const Preview = i18n.t('common.actions.preview');
export const AddField = i18n.t('forms.actions.addField');
export const AddFormElement = i18n.t('forms.actions.addFormElement');
export const CreateNewField = i18n.t('forms.actions.createNewField');
export const ChooseQualioResource = i18n.t('forms.actions.chooseQualioResource');
export const FieldTitle = i18n.t('forms.labels.fieldTitle');
export const HelpText = i18n.t('forms.labels.helpText');
export const AddHelpText = i18n.t('forms.actions.addHelpText');
export const EditHelpText = i18n.t('forms.actions.editHelpText');
export const SectionFieldTitle = i18n.t('forms.labels.sectionFieldTitle');
export const SectionDescription = i18n.t('forms.labels.sectionDescription');
export const AddSectionDescription = i18n.t('forms.actions.addSectionDescription');
export const EditSectionDescription = i18n.t('forms.actions.editSectionDescription');
export const DefaultContent = i18n.t('forms.labels.defaultContent');
export const AddDefaultContent = i18n.t('forms.actions.addDefaultContent');
export const EditDefaultContent = i18n.t('forms.actions.editDefaultContent');
export const MultiSelect = i18n.t('forms.labels.multiSelect');
export const OptionSectionLabel = i18n.t('forms.labels.optionSectionLabel');
export const AddOption = i18n.t('forms.actions.addOption');
export const OptionValueRequired = i18n.t('forms.validation.optionValueRequired');
export const OptionValueTooLarge = i18n.t('forms.validation.optionValueTooLarge');
export const OptionValueUnique = i18n.t('forms.validation.optionValueUnique');
export const NoFormYet = i18n.t('forms.messages.noFormYet');
export const StartFormCopy = i18n.t('forms.messages.startFormCopy');
export const StartBuildingForm = i18n.t('forms.actions.startBuildingForm');
export const PrefixHelpText = i18n.t('templates.helpText.prefixHelpText');
export const DefaultTimeLimitDefaultHelpText = i18n.t('templates.helpText.defaultTimeLimitDefaultHelpText');
export const DefaultTimeLimitEventDueDateHelpText = i18n.t('templates.helpText.defaultTimeLimitEventDueDateHelpText');
export const ValidationStepTimeDelayHelpText = i18n.t('templates.helpText.validationStepTimeDelayHelpText');

// Event Forms
export const CompleteForm = i18n.t('forms.actions.completeForm');
export const FormPreviewWarning = i18n.t('forms.messages.formPreviewWarning');
export const FormPreviewDuplicateLabelWarning = i18n.t('forms.messages.formPreviewDuplicateLabelWarning');
export const FormUpdateSuccessTitle = i18n.t('forms.messages.formUpdateSuccessTitle');
export const FormUpdateSuccessMessage = i18n.t('forms.messages.formUpdateSuccessMessage');
export const FormUpdateFailedTitle = i18n.t('forms.messages.formUpdateFailedTitle');
export const FormMissingMessage = i18n.t('forms.messages.formMissingMessage');
export const ApplyChanges = i18n.t('common.actions.applyChanges');
export const Add = i18n.t('common.actions.add');

export const ApproversUpdateSuccessTitle = (approverType: ApproverType) => {
  return i18n.t('forms.messages.approversUpdateSuccessTitle', { approverType: sentenceCase(approverType) });
};

export const DisabledDeleteTooltip = (fieldType: string) => {
  return i18n.t('forms.messages.disabledDeleteTooltip', { fieldType: sentenceCase(fieldType) });
};
export const DisableRequiredTooltip = (fieldType: string) => {
  return i18n.t('forms.messages.disableRequiredTooltip', { fieldType: sentenceCase(fieldType) });
};
export const ApproversUpdateSuccessMessage = (approverType: ApproverType) => {
  return i18n.t('forms.messages.approversUpdateSuccessMessage', { approverType: sentenceCase(approverType) });
};
export const ApproversUpdateFailedTitle = (approverType: ApproverType) => {
  return i18n.t('forms.messages.approversUpdateFailedTitle', { approverType: sentenceCase(approverType) });
};
export const ApproversUpdateFailedMessage = (approverType: ApproverType) => {
  return i18n.t('forms.messages.approversUpdateFailedMessage', { approverType });
};
export const AtLeastOneAssigneeRequiredMessage = (status: string) => {
  return i18n.t('forms.messages.atLeastOneAssigneeRequiredMessage', { status: sentenceCase(status).replace('_', ' ') });
};
export const RemovingActionedApproverErrorMessage = (approverType: ApproverType) => {
  return i18n.t('forms.messages.removingActionedApproverErrorMessage', {
    approverType: approverType.replace('_', ' '),
  });
};
export const ReviewForm = (formName: string) => i18n.t('forms.messages.reviewForm', { formName });

// Event Form Statuses
export const EventFormStatuses = {
  draft: i18n.t('common.statuses.draft'),
  for_review: i18n.t('common.statuses.forReview'),
  for_approval: i18n.t('common.statuses.forApproval'),
  approved: i18n.t('common.statuses.approved'),
  approval_declined: i18n.t('common.statuses.approvalDeclined'),
  effective: i18n.t('common.statuses.effective'),
  superseded: i18n.t('common.statuses.superseded'),
  cancelled: i18n.t('common.statuses.canceled'),
} as const;

// Events
export const EventMissingMessage = i18n.t('events.messages.eventMissingMessage');
export const CloseEarly = i18n.t('events.actions.closeEarly');
export const CreateEvent = i18n.t('events.actions.createEvent');
export const EditEvent = i18n.t('events.actions.editEvent');
export const OpenEvent = i18n.t('events.actions.openEvent');
export const UpdateEvent = i18n.t('events.actions.updateEvent');
export const UpdateTags = i18n.t('events.actions.updateTags');
export const EscalateAndCreateNewEvent = i18n.t('events.actions.escalateAndCreateNewEvent');
export const ExportEvent = i18n.t('events.actions.exportEvent');
export const ChangeOwner = i18n.t('events.actions.changeOwner');
export const UpdateEventProperties = i18n.t('events.actions.updateEventProperties');
export const ExportListAsXLSX = i18n.t('events.actions.exportListAsXLSX');
export const ExportListAsPDF = i18n.t('events.actions.exportListAsPDF');
export const ProvideAnyInformationYouHave = i18n.t('events.messages.provideAnyInformationYouHave');
export const EventCreated = i18n.t('common.notifications.eventCreated');
export const EventUpdated = i18n.t('common.notifications.eventUpdated');
export const EventNotUpdated = i18n.t('common.notifications.eventNotUpdated');
export const EventOpened = i18n.t('common.notifications.eventOpened');
export const EventNotOpened = i18n.t('common.notifications.eventNotOpened');
export const EventNotCreated = i18n.t('common.notifications.eventNotCreated');
export const OwnerChanged = i18n.t('common.notifications.ownerChanged');
export const OwnerNotChanged = i18n.t('common.notifications.ownerNotChanged');
export const SelectANewOwner = i18n.t('common.labels.selectANewOwner');
export const EventProperties = i18n.t('events.labels.eventProperties');
export const RelatedSuppliers = i18n.t('events.labels.relatedSuppliers');
export const RelatedEvents = i18n.t('events.labels.relatedEvents');
export const RelatedDocuments = i18n.t('events.labels.relatedDocuments');
export const SourceEvent = i18n.t('events.labels.sourceEvent');
export const EscalatedTo = i18n.t('events.labels.escalatedTo');
export const Risk = i18n.t('common.labels.risk');
export const RootCause = i18n.t('common.labels.rootCause');
export const Steps = i18n.t('common.labels.steps');
export const Tasks = i18n.t('common.labels.tasks');
export const DuplicatedStepNames = i18n.t('templates.messages.duplicatedStepNames');
export const EditEventTemplate = i18n.t('templates.actions.editEventTemplate');
export const EventTemplates = i18n.t('templates.labels.eventTemplates');
export const EventStatues: Record<EventStatus, string> = {
  open: i18n.t('events.statuses.open'),
  closed: i18n.t('events.statuses.closed'),
  imported: i18n.t('events.statuses.imported'),
  rejected: i18n.t('events.statuses.rejected'),
  cancelled: i18n.t('events.statuses.canceled'),
};
export const EventAcceptedMessage = i18n.t('events.messages.eventAcceptedMessage');
export const EventRejectedMessage = i18n.t('events.messages.eventRejectedMessage');
export const EventCanceledMessage = i18n.t('events.messages.eventCanceledMessage');
export const DefaultOwnerNoPermissionTitle = i18n.t('events.messages.defaultOwnerNoPermissionTitle');
export const DefaultOwnerNoPermissionMessage = i18n.t('events.messages.defaultOwnerNoPermissionMessage');
export const DefaultAssigneeNoPermissionTitle = i18n.t('events.messages.defaultAssigneeNoPermissionTitle');
export const DefaultAssigneeNoPermissionMessage = i18n.t('events.messages.defaultAssigneeNoPermissionMessage');
export const EventAcceptSubText = i18n.t('events.modals.eventAcceptSubText');
export const EventCancelSubText = i18n.t('events.modals.eventCancelSubText');
export const EventRejectSubText = i18n.t('events.modals.eventRejectSubText');

// Search
export const SearchFailed = i18n.t('search.errors.searchFailed');

// Export Utils
export const FileReady = i18n.t('common.exports.fileReady');
export const PreparingFileForDownload = i18n.t('common.exports.preparingFileForDownload');
export const FailedToExportTheFile = i18n.t('common.exports.failedToExportTheFile');
export const XLSX = i18n.t('common.exports.xlsx');
export const PDF = i18n.t('common.exports.pdf');

// Validation
export const RequiredFieldHelper = i18n.t('forms.validation.requiredFieldHelper');
export const FormContentInvalidError = i18n.t('forms.validation.formContentInvalidError');
export const EventPropertiesNotUpdatedError = i18n.t('events.messages.eventPropertiesNotUpdatedError');

// Products
export const DeleteProductConfirmation = (productName: string) => {
  return i18n.t('products.confirmations.deleteProductConfirmation', { productName });
};

// Root Causes
export const RootCauses = i18n.t('rootCauses.labels.rootCauses');
export const DeleteRootCauseConfirmation = (rootCauseName: string) => {
  return i18n.t('rootCauses.confirmations.deleteRootCauseConfirmation', { rootCauseName });
};

// Form Status Changes
export const SentForReview = i18n.t('forms.statusChanges.sentForReview');
export const SentForApproval = i18n.t('forms.statusChanges.sentForApproval');
export const RevertedToDraft = i18n.t('forms.statusChanges.revertedToDraft');
export const CannotSendForReview = i18n.t('forms.statusChanges.cannotSendForReview');
export const CannotSendForApproval = i18n.t('forms.statusChanges.cannotSendForApproval');
export const CannotRevertToDraft = i18n.t('forms.statusChanges.cannotRevertToDraft');
export const CannotUpdateTheForm = i18n.t('forms.statusChanges.cannotUpdateTheForm');
export const WasSentForReview = (formTitle: string) => i18n.t('forms.statusChanges.wasSentForReview', { formTitle });
export const WasSentForApproval = (formTitle: string) =>
  i18n.t('forms.statusChanges.wasSentForApproval', { formTitle });
export const WasRevertedToDraft = i18n.t('forms.statusChanges.wasRevertedToDraft');

// Approval Users
export const Reviewers = i18n.t('forms.approvers.reviewers');
export const QualityApprovers = i18n.t('forms.approvers.qualityApprovers');
export const OtherApprovers = i18n.t('forms.approvers.otherApprovers');

// Form Actions
export const SendForReview = i18n.t('forms.actions.sendForReview');
export const SendForApproval = i18n.t('forms.actions.sendForApproval');
export const ReassignStep = i18n.t('forms.actions.reassignStep');
export const ManageReviewers = i18n.t('forms.actions.manageReviewers');
export const ManageApprovers = i18n.t('forms.actions.manageApprovers');
export const RevertToDraft = i18n.t('forms.actions.revertToDraft');
export const MarkAsReviewed = i18n.t('forms.actions.markAsReviewed');
export const ReviewStep = i18n.t('forms.labels.reviewStep');
export const PleaseMarkThisStepAsReviewed = i18n.t('forms.messages.pleaseMarkThisStepAsReviewed');
export const ReviewersAdded = i18n.t('forms.messages.reviewersAdded');

// Form Errors
export const CannotSendForApprovalMissingRequiredFields = i18n.t(
  'forms.errors.cannotSendForApprovalMissingRequiredFields',
);
export const ClickHere = i18n.t('common.actions.clickHere');
export const MissingRequiredFieldsWithApproversPrefix = i18n.t('forms.errors.pleaseAddApproversBeforeSubmitting');
export const MissingRequiredFieldsPrefix = i18n.t('forms.errors.missingRequiredFieldsOnEvent');
export const ToViewRequiredEventFieldsSuffix = i18n.t('forms.errors.toViewTheRequiredEventFields');
